import { supabase } from "@/integrations/supabase/client";
import type { ComandaCartLine } from "@/hooks/useSales";

/** Qual origem trouxe o usuário ao PDV. Decide o texto da faixa de contexto. */
export type ResumeSaleContext = "planCharge" | "stayComanda";

export interface ResumeSalePrefill {
  petId: string;
  ownerId: string | null;
  petName: string;
  ownerName: string;
  reservationId?: string | null;
}

/** O que qualquer tela põe em `location.state.resumeSale` para abrir o PDV. */
export interface ResumeSaleState {
  saleId: string;
  /** Atalho: quem já tem pet e responsável em mãos evita a consulta. */
  prefill?: ResumeSalePrefill;
  /** Caminho de origem, para o PDV oferecer o retorno depois de receber. */
  returnTo?: string;
  context?: ResumeSaleContext;
}

export interface ResumeSaleData {
  saleId: string;
  lines: ComandaCartLine[];
  prefill: ResumeSalePrefill;
  context: ResumeSaleContext;
  returnTo: string | null;
}

export type ResumeSaleFailure = "not_found" | "already_paid" | "load_failed";

export class ResumeSaleError extends Error {
  constructor(public readonly reason: ResumeSaleFailure) {
    super(`resume_sale_${reason}`);
    this.name = "ResumeSaleError";
  }
}

type SaleRow = {
  status: string | null;
  pet_id: string | null;
  owner_id: string | null;
  pet: { name: string | null } | null;
  owner: { name: string | null } | null;
};

async function loadPrefillFromSale(saleId: string): Promise<ResumeSalePrefill> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("sales")
    .select("status, pet_id, owner_id, pet:pets(name), owner:owners(name)")
    .eq("id", saleId)
    .maybeSingle();

  if (error) throw new ResumeSaleError("load_failed");
  if (!data) throw new ResumeSaleError("not_found");

  const row = data as SaleRow;
  // Retomar significa fechar a venda que já existe. Uma venda PAGA não tem o
  // que retomar, e reabri-la no PDV duplicaria o recebimento.
  if (row.status !== "ABERTA") throw new ResumeSaleError("already_paid");

  return {
    petId: row.pet_id ?? "",
    ownerId: row.owner_id,
    petName: row.pet?.name ?? "",
    ownerName: row.owner?.name ?? "",
  };
}

async function loadLines(saleId: string): Promise<ComandaCartLine[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any)
    .from("sale_items")
    .select("item_id, item_name, quantity, unit_price")
    .eq("sale_id", saleId);

  if (error) throw new ResumeSaleError("load_failed");

  const rows = (data ?? []) as Array<{
    item_id: string | null;
    item_name: string;
    quantity: number;
    unit_price: number;
  }>;

  return rows.map((row) => ({
    itemId: row.item_id,
    name: row.item_name,
    quantity: row.quantity,
    unitPrice: row.unit_price,
  }));
}

/**
 * Carrega a venda em aberto que o PDV vai retomar.
 *
 * Os itens vêm sempre do banco: o carrinho é verdade do servidor, nunca do
 * state de navegação, que pode estar velho. Pet e responsável aceitam atalho
 * via `prefill` porque quem clicou normalmente já os tem na tela.
 */
export async function loadResumeSale(state: ResumeSaleState): Promise<ResumeSaleData> {
  const lines = await loadLines(state.saleId);
  const prefill = state.prefill ?? (await loadPrefillFromSale(state.saleId));

  return {
    saleId: state.saleId,
    lines,
    prefill,
    context: state.context ?? "stayComanda",
    returnTo: state.returnTo ?? null,
  };
}
